import React, { useMemo, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { getControlledCloudGrindTestCampaignId, isControlledPaidDiscoveryEnabled } from '../services/cloudSchema';

const STAGE13_CAMPAIGN_ID = 'cloud-grind-controlled-stage13-2026-09-27';
const CREATOR_ID = 'tukDU5POTDSKgEn0SJy3VGDAyzx2';
const CREATOR_EMAIL = 'test123@gmail.com';
const VIEWER_ID = 'k8XyaBaJRuU7EuN5p2SMLG0HJMU2';
const VIEWER_EMAIL = 'today@gmail.com';
const FUNDING_AMOUNT = 4;
const REQUEST_ID = 'stage13-prep-one-completion';

function Stage13Control({ userProfile, user, onSignOut }) {
  const [status, setStatus] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const writeActionsArmed = new URLSearchParams(window.location.search).get('armed') === 'stage13-live';
  const configuredCampaignId = getControlledCloudGrindTestCampaignId();
  const exactGate = isControlledPaidDiscoveryEnabled() && configuredCampaignId === STAGE13_CAMPAIGN_ID;

  const role = useMemo(() => {
    if (user?.uid === CREATOR_ID) return 'creator';
    if (user?.uid === VIEWER_ID) return 'viewer';
    return 'unexpected';
  }, [user?.uid]);

  const runReadOnlyCheck = async () => {
    setStatus({ type: 'info', message: 'Checking production state without writing...' });
    const ids = {
      campaign: STAGE13_CAMPAIGN_ID,
      funding: STAGE13_CAMPAIGN_ID,
      claim: `${STAGE13_CAMPAIGN_ID}_${VIEWER_ID}`,
      fundingTransfer: `${CREATOR_ID}_${STAGE13_CAMPAIGN_ID}_${REQUEST_ID}`,
      fundingLedger: `campaign_funding_transfer_${CREATOR_ID}_${STAGE13_CAMPAIGN_ID}_${REQUEST_ID}`,
      spendLedger: `campaign_discovery_spend_${STAGE13_CAMPAIGN_ID}_${VIEWER_ID}`,
      rewardLedger: `discovery_reward_${STAGE13_CAMPAIGN_ID}_${VIEWER_ID}`
    };

    const [creatorSnap, viewerSnap, campaignSnap, claimSnap, fundingLedgerSnap, spendLedgerSnap, rewardLedgerSnap] =
      await Promise.all([
        getDoc(doc(db, 'users', CREATOR_ID)),
        getDoc(doc(db, 'users', VIEWER_ID)),
        getDoc(doc(db, 'campaigns', ids.campaign)),
        getDoc(doc(db, 'campaignRewardClaims', ids.claim)),
        getDoc(doc(db, 'tokenLedger', ids.fundingLedger)).catch(() => null),
        getDoc(doc(db, 'tokenLedger', ids.spendLedger)).catch(() => null),
        getDoc(doc(db, 'tokenLedger', ids.rewardLedger)).catch(() => null)
      ]);

    const creator = creatorSnap.exists() ? creatorSnap.data() : null;
    const viewer = viewerSnap.exists() ? viewerSnap.data() : null;
    const result = {
      exactGate,
      configuredCampaignId,
      creator: creator && {
        uid: creator.uid,
        email: creator.email,
        tokens: creator.tokens,
        totalEarned: creator.totalEarned
      },
      viewer: viewer && {
        uid: viewer.uid,
        email: viewer.email,
        tokens: viewer.tokens,
        totalEarned: viewer.totalEarned,
        interestIds: viewer.interestIds || []
      },
      existing: {
        campaign: campaignSnap.exists(),
        claim: claimSnap.exists(),
        fundingLedger: fundingLedgerSnap?.exists?.() === true,
        spendLedger: spendLedgerSnap?.exists?.() === true,
        rewardLedger: rewardLedgerSnap?.exists?.() === true
      }
    };

    result.pass =
      result.exactGate &&
      result.creator?.tokens >= FUNDING_AMOUNT &&
      result.viewer?.interestIds?.includes('readers') &&
      !result.existing.campaign &&
      !result.existing.claim &&
      !result.existing.fundingLedger &&
      !result.existing.spendLedger &&
      !result.existing.rewardLedger;

    setPreflight(result);
    setStatus({
      type: result.pass ? 'success' : 'error',
      message: result.pass
        ? 'Read-only preflight passes. Write actions remain locked until the live run is explicitly armed.'
        : 'Read-only preflight failed. Stop before any production write.'
    });
  };

  const seedCampaign = async () => {
    assertCanWrite(role, exactGate, writeActionsArmed);
    const campaignRef = doc(db, 'campaigns', STAGE13_CAMPAIGN_ID);
    await setDoc(campaignRef, {
      source: 'cloud',
      localOriginId: 'controlled-cloud-grind-test',
      creatorId: CREATOR_ID,
      creatorName: 'PromoGrind Stage 13 Creator',
      creatorType: 'Controlled Test',
      platform: 'Book',
      title: 'PromoGrind Stage 13 Controlled Walkthrough',
      message: 'Controlled live campaign for one paid Discovery completion.',
      status: 'Active',
      deliveryStatus: 'insufficient_funds',
      deliveryPauseReason: 'Paused - Add Tokens to Resume',
      fundingStatus: 'insufficient_funds',
      targetInterestIds: ['readers'],
      completionTokenRate: FUNDING_AMOUNT,
      tokenSpendTotal: 0,
      completionCount: 0,
      paidCompletionCount: 0,
      destination: {
        platformId: 'amazon',
        label: 'Amazon',
        trusted: true,
        kind: 'book',
        url: 'https://www.amazon.com/'
      },
      feedbackQuestion: 'Did this controlled Stage 13 campaign display correctly?',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      publishedAt: serverTimestamp()
    });
    setStatus({ type: 'success', message: 'Seeded the exact Stage 13 controlled campaign.' });
  };

  const fundCampaign = async () => {
    assertCanWrite(role, exactGate, writeActionsArmed);
    const fund = httpsCallable(functions, 'fundCampaignWithTokens');
    const result = await fund({
      campaignId: STAGE13_CAMPAIGN_ID,
      amount: FUNDING_AMOUNT,
      requestId: REQUEST_ID
    });
    setStatus({ type: 'success', message: `Funded: ${JSON.stringify(result.data)}` });
  };

  return (
    <div style={pageStyle}>
      <h1>Stage 13 Controlled Live Walkthrough</h1>
      <p style={noticeStyle}>
        Local-only control page. Do not use outside the controlled Stage 13 run.
      </p>

      <section style={panelStyle}>
        <h2>Signed-in account</h2>
        <p><strong>Email:</strong> {user?.email}</p>
        <p><strong>UID:</strong> {user?.uid}</p>
        <p><strong>Role:</strong> {role}</p>
        <p><strong>Balance:</strong> {Number(userProfile?.tokens || 0).toLocaleString()} tokens</p>
        <p><strong>Total earned:</strong> {Number(userProfile?.totalEarned || 0).toLocaleString()}</p>
        <button type="button" onClick={onSignOut} style={secondaryButtonStyle}>Sign Out</button>
      </section>

      <section style={panelStyle}>
        <h2>Gate</h2>
        <p><strong>Expected campaign:</strong> {STAGE13_CAMPAIGN_ID}</p>
        <p><strong>Configured campaign:</strong> {configuredCampaignId || '(not configured)'}</p>
        <p><strong>Exact controlled paid gate:</strong> {exactGate ? 'yes' : 'no'}</p>
        <p><strong>Write actions armed:</strong> {writeActionsArmed ? 'yes' : 'no'}</p>
      </section>

      <section style={panelStyle}>
        <h2>Sequence</h2>
        <ol>
          <li>Sign in here as {CREATOR_EMAIL}; verify creator UID and balance.</li>
          <li>Sign out, then sign in as {VIEWER_EMAIL}; verify viewer UID and `readers` interest.</li>
          <li>Only after both sign-ins are confirmed, reload armed and sign in as creator.</li>
          <li>Seed only {STAGE13_CAMPAIGN_ID}.</li>
          <li>Fund exactly 4 tokens through `fundCampaignWithTokens`.</li>
          <li>Sign in as viewer and complete the controlled Discovery once.</li>
        </ol>
      </section>

      <section style={panelStyle}>
        <h2>Read-only preflight</h2>
        <button type="button" onClick={runReadOnlyCheck} style={primaryButtonStyle}>Run Read-only Check</button>
        {preflight && <pre style={preStyle}>{JSON.stringify(preflight, null, 2)}</pre>}
      </section>

      <section style={panelStyle}>
        <h2>Locked live actions</h2>
        <button type="button" onClick={seedCampaign} disabled={!writeActionsArmed || role !== 'creator' || !exactGate} style={dangerButtonStyle}>
          Seed exact campaign
        </button>
        <button type="button" onClick={fundCampaign} disabled={!writeActionsArmed || role !== 'creator' || !exactGate} style={dangerButtonStyle}>
          Fund exactly 4 tokens
        </button>
      </section>

      {status && (
        <div style={statusStyle(status.type)} role="status">
          {status.message}
        </div>
      )}
    </div>
  );
}

function assertCanWrite(role, exactGate, armed) {
  if (!armed) throw new Error('Live actions are not armed.');
  if (role !== 'creator') throw new Error('Sign in as the Stage 13 creator first.');
  if (!exactGate) throw new Error('Controlled paid Discovery gate is not exact.');
}

const pageStyle = {
  maxWidth: 960,
  margin: '0 auto',
  padding: 24,
  color: '#1f2937'
};

const panelStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #d8dee6',
  borderRadius: 8,
  marginBottom: 16,
  padding: 18
};

const noticeStyle = {
  backgroundColor: '#fffbeb',
  border: '1px solid #f59e0b',
  borderRadius: 8,
  color: '#92400e',
  fontWeight: 700,
  padding: 12
};

const primaryButtonStyle = {
  backgroundColor: '#2563eb',
  border: 'none',
  borderRadius: 8,
  color: '#ffffff',
  cursor: 'pointer',
  fontWeight: 800,
  padding: '11px 14px'
};

const secondaryButtonStyle = {
  ...primaryButtonStyle,
  backgroundColor: '#64748b'
};

const dangerButtonStyle = {
  ...primaryButtonStyle,
  backgroundColor: '#991b1b',
  marginRight: 10
};

const preStyle = {
  backgroundColor: '#0f172a',
  borderRadius: 8,
  color: '#e2e8f0',
  overflow: 'auto',
  padding: 12
};

const statusStyle = (type) => ({
  backgroundColor: type === 'success' ? '#dcfce7' : type === 'error' ? '#fee2e2' : '#dbeafe',
  border: `1px solid ${type === 'success' ? '#16a34a' : type === 'error' ? '#dc2626' : '#2563eb'}`,
  borderRadius: 8,
  bottom: 16,
  color: '#111827',
  fontWeight: 800,
  left: 16,
  padding: 12,
  position: 'fixed',
  right: 16
});

export default Stage13Control;
